// Garment photographs are stored as full-resolution PNGs — a single one is
// ~2.4MB, and a dress page renders five of them. Netlify's Image CDN can
// resize and re-encode on the fly (2.4MB PNG -> ~74KB WebP at 600px), so the
// front end asks for the width it will actually display instead of the width
// the photographer exported.
//
// Draft images are deliberately excluded: they sit behind requireUser, and the
// CDN fetches the source server-side without the admin's token, so it would
// get a 401. The admin previews those through useAuthedImage instead.

const DEFAULT_QUALITY = 78;

const isPublished = (rawUrl) =>
  typeof rawUrl === 'string' && (rawUrl.includes('published%2F') || rawUrl.includes('published/'));

// One CDN URL at a specific width.
export function cdnImageUrl(rawUrl, width, { quality = DEFAULT_QUALITY, format = 'webp' } = {}) {
  if (!rawUrl || !isPublished(rawUrl)) return rawUrl;
  const params = new URLSearchParams({
    url: rawUrl,
    w: String(width),
    fm: format,
    q: String(quality),
  });
  return `/.netlify/images?${params.toString()}`;
}

// Everything an <img> needs. `sizes` should describe the CSS width the image
// occupies at each breakpoint so the browser can pick sensibly — get it wrong
// and it will over-fetch, which defeats the point.
export function responsiveImage(rawUrl, widths, sizes) {
  if (!rawUrl || !isPublished(rawUrl)) {
    return { src: rawUrl || '', srcSet: undefined, sizes: undefined };
  }
  return {
    // Middle width as the src, so a browser without srcset support still gets
    // something reasonable rather than the largest variant.
    src: cdnImageUrl(rawUrl, widths[Math.floor(widths.length / 2)]),
    srcSet: widths.map((w) => `${cdnImageUrl(rawUrl, w)} ${w}w`).join(', '),
    sizes,
  };
}

// Shared width ladders, so call sites don't each invent their own.
//
// Rungs are placed for 2x displays, which is what most phones and every
// retina laptop report: a 292px card needs 584 device pixels, so a ladder
// that steps 560 -> 800 makes the browser jump a rung and over-fetch by 40%.
// Four rungs is deliberate — each additional one is another cold transform
// on the CDN (~2.5s the first time anyone requests that exact size).
export const CARD_WIDTHS = [300, 450, 640, 900];
// Top rung sits a little above the current masters (1023px) so there is
// headroom if they are ever re-exported larger, without minting variants far
// beyond anything the source can actually fill.
export const GALLERY_WIDTHS = [480, 720, 1000, 1280];
export const COVER_WIDTHS = [340, 520, 780, 1040];
